import { GeoPointFeature } from '../../..'

/** Uma análise disponível para desconto dentro do popover de seleção em massa. */
export interface AnaliseDescontavel {
    key: string
    cod_analise: number
    analise_nome: string
    profundidade: string
    cor: string
    qtd: number
    pontos: GeoPointFeature[]
}

/** Seção do popover: uma profundidade e as análises que existem nela. */
export interface GrupoProfundidade {
    profundidade: string
    itens: AnaliseDescontavel[]
}

/**
 * Chave de identidade de um ponto da malha.
 * Usa NOME por ser a mesma chave que selecionarPontosMalha/moverPontosMalha adotam.
 */
export function chavePonto(ponto: GeoPointFeature): string {
    return String(ponto?.NOME)
}

/**
 * Une as análises adicionadas no modal com as já salvas no formulário e agrupa por profundidade.
 *
 * O desconto é somente leitura: nenhuma dessas análises é alterada, elas apenas informam
 * quais pontos já foram usados para que o usuário possa retirá-los da nova seleção.
 * Análises repetidas (mesma análise + profundidade nas duas listas) aparecem uma única vez.
 */
export function agruparAnalisesPorProfundidade(
    adicionadas: any[],
    salvas: any[]
): GrupoProfundidade[] {
    const todas = [...(adicionadas ?? []), ...(salvas ?? [])]

    const porChave = new Map<string, AnaliseDescontavel>()

    todas.forEach((item) => {
        const pontos: GeoPointFeature[] = item?.pontos_selecionados ?? []
        if (pontos.length === 0) return

        const profundidade = item?.profundidade ?? '—'
        const key = `${item?.cod_analise}__${profundidade}`

        // A primeira ocorrência vence; a mesma análise nas duas listas tem os mesmos pontos.
        if (porChave.has(key)) return

        porChave.set(key, {
            key,
            cod_analise: Number(item?.cod_analise),
            analise_nome: item?.analise_nome ?? '',
            profundidade,
            cor: item?.cor ?? '#cccccc',
            qtd: pontos.length,
            pontos,
        })
    })

    const grupos = new Map<string, AnaliseDescontavel[]>()

    Array.from(porChave.values()).forEach((analise) => {
        const atual = grupos.get(analise.profundidade) ?? []
        atual.push(analise)
        grupos.set(analise.profundidade, atual)
    })

    return Array.from(grupos.entries())
        .map(([profundidade, itens]) => ({
            profundidade,
            itens: itens.sort((a, b) => a.analise_nome.localeCompare(b.analise_nome)),
        }))
        .sort((a, b) => a.profundidade.localeCompare(b.profundidade))
}

/**
 * Chaves dos pontos que já pertencem a alguma análise — adicionadas no modal ou já salvas.
 *
 * Alimenta o terceiro estado do marker ("já usado por outra análise") e a contagem de
 * pontos nunca usados. Reaproveita a mesma leitura de `agruparAnalisesPorProfundidade`
 * para que o desconto do popover e o mapa nunca discordem sobre o que já foi usado.
 */
export function chavesPontosJaUsados(adicionadas: any[], salvas: any[]): Set<string> {
    const usados = new Set<string>()

    ;[...(adicionadas ?? []), ...(salvas ?? [])].forEach((item) => {
        ;(item?.pontos_selecionados ?? []).forEach((ponto: GeoPointFeature) => {
            usados.add(chavePonto(ponto))
        })
    })

    return usados
}

export interface ResultadoSelecaoEmMassa {
    /** Pontos que ficarão selecionados ao confirmar. */
    pontosParaSelecionar: GeoPointFeature[]
    /** Pontos reais descontados (união distinta), não a soma das análises. */
    qtd_descontados: number
    /** Área do talhão dividida pelos pontos que serão selecionados. */
    grid: number
}

/**
 * Calcula o resultado da seleção em massa.
 *
 * Nenhuma análise marcada => seleciona a malha inteira (equivale ao antigo
 * "Selecionar todos os pontos"). Cada análise marcada retira seus pontos da seleção.
 *
 * A contagem de descontados é a união distinta por NOME: análises diferentes podem
 * compartilhar pontos, então somar as quantidades contaria o mesmo ponto duas vezes.
 */
export function calcularSelecaoEmMassa(params: {
    pontosDaMalha: GeoPointFeature[]
    marcadas: AnaliseDescontavel[]
    talhao_area?: number | string
}): ResultadoSelecaoEmMassa {
    const { pontosDaMalha, marcadas, talhao_area } = params

    const malha = pontosDaMalha ?? []

    const descontar = new Set<string>()
    ;(marcadas ?? []).forEach((analise) => {
        ;(analise?.pontos ?? []).forEach((ponto) => descontar.add(chavePonto(ponto)))
    })

    const pontosParaSelecionar = malha.filter((ponto) => !descontar.has(chavePonto(ponto)))

    // Conta só o que realmente saiu da malha — um ponto de análise que não existe
    // mais na malha atual não deve ser contabilizado como descontado.
    const qtd_descontados = malha.length - pontosParaSelecionar.length

    const area = Number(talhao_area ?? 0)
    const grid = pontosParaSelecionar.length > 0 ? area / pontosParaSelecionar.length : 0

    return { pontosParaSelecionar, qtd_descontados, grid }
}
